import { PropsWithChildren, ReactNode } from "react";
import { Lock, Sparkles } from "lucide-react";
import { Features, FEATURE_MIN_TIER, PLAN_LABELS } from "./plans";
import { useMarketplace } from "./context";

type FeatureKey = keyof Omit<Features, "maxLinks">;

interface LockedOverlayProps {
  feature: FeatureKey;
  compact?: boolean;
}

export const UpgradeBadge = ({
  feature,
  compact = false,
}: LockedOverlayProps) => {
  const minTier = FEATURE_MIN_TIER[feature];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md font-semibold ${
        compact
          ? "h-4 px-1 text-[9px]"
          : "h-5 px-1.5 text-[10px]"
      } bg-gradient-to-r from-amber-100 to-amber-50 text-amber-800 ring-1 ring-amber-200/60`}
    >
      <Sparkles size={compact ? 8 : 9} />
      {PLAN_LABELS[minTier]}
    </span>
  );
};

interface FeatureGateProps extends PropsWithChildren {
  feature: FeatureKey;
  // What to render when the feature is NOT enabled. If omitted, children are
  // rendered but visually locked (disabled-look + badge handled by caller).
  fallback?: ReactNode;
  // If true, wrap children in a disabled-look overlay with lock icon.
  disabledOverlay?: boolean;
}

// Conditionally renders children based on the current tier's features.
// - allowed → render children
// - not allowed + fallback → render fallback
// - not allowed + disabledOverlay → render children inside a locked overlay
// - not allowed + neither → render nothing
const FeatureGate = ({
  feature,
  fallback,
  disabledOverlay,
  children,
}: FeatureGateProps) => {
  const { features } = useMarketplace();
  const allowed = !!features[feature];

  if (allowed) return <>{children}</>;
  if (fallback) return <>{fallback}</>;

  if (disabledOverlay) {
    return (
      <div className="relative" aria-disabled="true">
        <div className="pointer-events-none select-none opacity-50 filter saturate-50">
          {children}
        </div>
        <div className="absolute inset-0 rounded-xl bg-white/40 backdrop-blur-[1px] flex items-center justify-center gap-2 text-xs font-medium text-ink-700">
          <Lock size={13} className="text-ink-500" />
          <span>
            Available in {PLAN_LABELS[FEATURE_MIN_TIER[feature]]}
          </span>
        </div>
      </div>
    );
  }
  return null;
};

export default FeatureGate;

// Hook returning whether a feature is enabled and the min tier required.
export const useFeature = (feature: FeatureKey) => {
  const { features } = useMarketplace();
  return {
    allowed: !!features[feature],
    minTier: FEATURE_MIN_TIER[feature],
    minTierLabel: PLAN_LABELS[FEATURE_MIN_TIER[feature]],
  };
};
